import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'

import Text from '../../ui/Text'
import findChargingConnector from '../findChargingConnector'
import findSchukoConnector from '../findSchukoConnector'
import { getConfigValue } from '../get-config-value'
import { remoteStartIsAvailable, remoteStopIsAvailable } from '../get-connector-state'
import isSchukoEnabled from '../isSchukoEnabled'
import { IConfigKey } from '../loadOcppConfigKeys'
import { KEY_FREE_CHARGING } from '../ocpp-aura-connector-control'
import { RemoteControlRow } from '../remote-control-row'
import { RemoteControlWidget } from '../remote-control-widget'
import { RemoteOcppDimmerControl } from '../remote-ocpp-dimmer-control'
import setOcppConfigValue from '../setOcppConfigValue'
import {
    ChargePoint,
    useChargePointOverrideScheduleForOngoingChargingSessionMutation,
    useChargePointRemoteStartMutation,
    useChargePointRemoteStopMutation,
    useOcppCommandChangeAvailabilityMutation,
    useOcppCommandUpdateConfigKeyMutation,
    useGreenChargingChargePointStatusQuery,
    useSmartChargingScheduleGetByChargePointQuery,
    useSmartChargingScheduleUpdateMutation,
} from '../../common/redux/api/web-api'
import { Toggle } from '../../common/components/Toggle'
import { SelectRFIDTagModal } from '../../common/components/select-rfid-tag-modal'
import ConnectorStatus from '../connector-status'
import { isAdminDomain, isSupportDomain, isUserappDomain } from '../currentDomain'
import { onGoingTransactionStates } from '../charge-point-dawn-control-widget'
import { showErrorMessagesForIgnoreSchedule } from '../../common/utils/chargePointUtils'
import useText from '../../common/hooks/useText'
import { useToast } from '../../common/uikit/ToastProvider'
import { isFetchBaseQueryError } from '../../common/redux/api/fetchBaseQueryWithReauth'

export const SSocketIcon = styled.span`
    left: 4px;
    font-size: 19px;
    position: relative;
    top: 3px;
`

export const SLightIcon = styled.span`
    font-size: 21px;
    position: relative;
    top: 0;
    left: 4px;
`

const KEY_DOWN_LIGHT = 'Downlight'

enum LightState {
    ON = '1',
    OFF = '0',
}

enum NfcLockState {
    ON = 'false',
    OFF = 'true',
}

export type ChargePointOcppHaloControlWidgetProps = {
    isReadOnly: boolean
    chargePoint: ChargePoint
}

export function ChargePointOcppHaloControlWidget({
    isReadOnly = false,
    chargePoint,
}: ChargePointOcppHaloControlWidgetProps): React.ReactElement {
    const text = useText()
    const [configKeys, setConfigKeys] = useState<IConfigKey[]>(chargePoint.ocppConfigKeys)
    const [chargingConnector, setChargingConnector] = useState(findChargingConnector(chargePoint.connectors ?? []))
    const [socketConnector, setSocketConnector] = useState(findSchukoConnector(chargePoint.connectors ?? []))
    const toast = useToast()

    const { data: schedules } = useSmartChargingScheduleGetByChargePointQuery({ chargePointId: chargePoint.id })

    const getScheduleForConnector = (connectorId: number) => {
        return schedules?.find((schedule) => schedule.connectorId === connectorId)
    }
    const checkScheduleIsActive = (connectorId: number) => {
        return getScheduleForConnector(connectorId)?.isActive ?? false
    }

    const handleScheduleUpdate = (connectorId: number) => () => {
        const schedule = getScheduleForConnector(connectorId)
        if (!schedule)
            return confirmDialog({
                header: text('msgNoSchedulesHeader'),
                message: text('msgNoSchedulesText'),
                acceptClassName: 'hidden',
                rejectLabel: text('btnClose'),
            })

        updateSchedule({
            smartChargingSchedule: {
                ...schedule,
                isActive: !schedule?.isActive,
            },
        })
            .unwrap()
            .catch((error: unknown) => {
                if (isFetchBaseQueryError(error)) {
                    if (error.status === 410) {
                        return toast.notifyError(text('msgOfflineChargerScheduleIsNotEditable'))
                    }
                } else {
                    toast.notifyError(text('msgFailedToUpdateSchedule'))
                }
            })
    }

    const [changeAvailability] = useOcppCommandChangeAvailabilityMutation()
    const [saveConfigKey] = useOcppCommandUpdateConfigKeyMutation()
    const [remoteStart] = useChargePointRemoteStartMutation()
    const [remoteStop] = useChargePointRemoteStopMutation()
    const [overrideSchedule] = useChargePointOverrideScheduleForOngoingChargingSessionMutation()
    const [updateSchedule] = useSmartChargingScheduleUpdateMutation()

    const [selectRfidModalIsOpen, setSelectRfidModalIsOpen] = useState(false)
    const isOnline = chargePoint.chargePointStatus !== 'Offline'
    const isLightOn = getConfigValue(KEY_DOWN_LIGHT, configKeys) === LightState.ON
    const isNfcLockOn = getConfigValue(KEY_FREE_CHARGING, configKeys) === NfcLockState.ON
    const { data: chargePointStatus } = useGreenChargingChargePointStatusQuery({ chargePointId: chargePoint.id })

    const remoteStartIsVisible = useMemo(() => remoteStartIsAvailable(chargingConnector), [chargingConnector])
    const remoteStopIsVisible = useMemo(() => remoteStopIsAvailable(chargingConnector), [chargingConnector])

    useEffect(() => {
        setConfigKeys(chargePoint.ocppConfigKeys)
        setChargingConnector(findChargingConnector(chargePoint.connectors ?? []))
        setSocketConnector(findSchukoConnector(chargePoint.connectors ?? []))
    }, [chargePoint])

    const toggleOcppConfigValue = async (key: string, value: string) => {
        const updatedConfigKeys = setOcppConfigValue(key, value, configKeys)
        if (!updatedConfigKeys) return
        await saveConfigKey({
            chargePointId: chargePoint.id,
            keyValue: updatedConfigKeys.find((configKeys) => configKeys.key === key),
        })
            .unwrap()
            .then(() => setConfigKeys(updatedConfigKeys))
            .catch(() => toast.notifyError(text('msgRemoteControlCommandFailed')))
    }

    const handleRemoteStart = async (rfidTag?: string) => {
        await remoteStart({
            chargePointId: chargePoint.id,
            connectorId: 1, // Halo has only one connector
            rfidTag,
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('msgRemoteStartSuccess'))
            })
            .catch(() => {
                toast.notifyError(text(isAdminDomain ? 'msgRemoteStartAdminFailed' : 'msgRemoteStartFailed'))
            })
    }

    const handleRemoteControl = async () => {
        if (remoteStopIsVisible) {
            await remoteStop({
                chargePointId: chargePoint.id,
                connectorId: 1, //Halo has only one connector
            })
                .unwrap()
                .then(() => {
                    toast.notifySuccess(text('msgRemoteStopSuccess'))
                })
                .catch(() => {
                    toast.notifyError(text(isAdminDomain ? 'msgRemoteStopAdminFailed' : 'msgRemoteStopAdminFailed'))
                })
        } else {
            if (isNfcLockOn) {
                setSelectRfidModalIsOpen(true)
            } else if (chargingConnector?.defaultNfcTagId) {
                handleRemoteStart(chargingConnector?.defaultNfcTagId)
            } else {
                toast.notifyError(text('msgRemoteStartFailed'))
            }
        }
    }

    const handleOverrideSchedule = async () => {
        await overrideSchedule({
            chargePointId: chargePoint.id,
            connectorId: 1,
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('msgIgnoreScheduleSuccess'))
            })
            .catch((error) => {
                toast.notifyError(showErrorMessagesForIgnoreSchedule(error.data.error))
            })
    }
    const showStartStopChargingButton = !isUserappDomain && isOnline && (remoteStartIsVisible || remoteStopIsVisible)
    const showIgnoreScheduleButton =
        !isUserappDomain && isOnline && remoteStopIsVisible && chargingConnector?.hasSchedule

    return (
        <RemoteControlWidget
            greenChargingStatus={chargePointStatus?.greenChargingStatus}
            isReadOnly={isReadOnly}
            chargePoint={chargePoint}
            title={'lblHaloControlOcppWidget'}
        >
            {chargingConnector && (
                <RemoteControlRow
                    title={<div data-testid={`chargerText-${chargePoint.id}`}>{text('lblCharger')}</div>}
                    button={
                        <Toggle
                            isToggled={chargingConnector.onByUser}
                            disabled={isReadOnly || !isOnline || chargePoint?.ocppEnabled}
                            toggleFn={() =>
                                changeAvailability({
                                    chargePointId: chargePoint.id,
                                    changeAvailabilityReq: {
                                        connectorId: chargingConnector.connectorId ?? 1,
                                        type: chargingConnector.onByUser ? 'Inoperative' : 'Operative',
                                    },
                                })
                                    .unwrap()
                                    .then(() =>
                                        setChargingConnector((prevValue) => ({
                                            ...prevValue,
                                            onByUser: !prevValue?.onByUser,
                                            offByUser: !prevValue?.offByUser,
                                        }))
                                    )
                                    .catch(() => toast.notifyError(text('msgRemoteControlCommandFailed')))
                            }
                            data-testid={`charger-${chargePoint.id}`}
                        />
                    }
                    remoteChargingControl={
                        <div className="flex gap-2 flex-column sm:flex-row">
                            {showStartStopChargingButton && (
                                <Button
                                    tooltip={
                                        remoteStartIsVisible ? text('lblStartChargingInfo') : text('lblStopSession')
                                    }
                                    size="small"
                                    data-testid="remote-control-button"
                                    onClick={handleRemoteControl}
                                    label={text(remoteStopIsVisible ? 'lblStop' : 'lblStart')}
                                    outlined
                                    severity="info"
                                    icon={remoteStopIsVisible ? 'pi pi-stop-circle' : 'pi pi-play-circle'}
                                />
                            )}
                            {showIgnoreScheduleButton && (
                                <Button
                                    tooltip={text('lblIgnoreSchedule')}
                                    style={{ padding: '6px' }}
                                    data-testid="remote-control-button-ignoreSchedule"
                                    onClick={handleOverrideSchedule}
                                    outlined
                                    severity="info"
                                    icon="pi pi-calendar-times"
                                />
                            )}
                        </div>
                    }
                    status={
                        isOnline && (
                            <ConnectorStatus
                                connector={chargingConnector}
                                isOcpp={chargePoint?.isOcpp}
                                key={chargingConnector?.connectorId}
                                showConsumption={true}
                                showErrorStatus={true}
                                productId={chargePoint?.productId}
                            />
                        )
                    }
                />
            )}
            {isSupportDomain ? (
                <RemoteControlRow
                    title={
                        <div data-testid={`chargerScheduleText-${chargePoint.id}`}>{text('lblChargerSchedule')}</div>
                    }
                    button={
                        <Toggle
                            toggleFn={handleScheduleUpdate(chargingConnector?.connectorId ?? 1)}
                            isToggled={checkScheduleIsActive(chargingConnector?.connectorId ?? 1)}
                            disabled={isReadOnly || !isOnline || chargePoint?.ocppEnabled}
                            data-testid={
                                checkScheduleIsActive(chargingConnector?.connectorId ?? 1)
                                    ? `chargerSchedule-On-${chargePoint.id}`
                                    : `chargerSchedule-Off-${chargePoint.id}`
                            }
                        />
                    }
                />
            ) : null}

            {isSchukoEnabled(chargePoint) && socketConnector && (
                <>
                    <RemoteControlRow
                        title={
                            <div data-testid={`socketText-${chargePoint.id}`}>
                                <Text code="lblSocket" />
                            </div>
                        }
                        button={
                            <Toggle
                                toggleFn={() =>
                                    changeAvailability({
                                        chargePointId: chargePoint.id,
                                        changeAvailabilityReq: {
                                            connectorId: socketConnector.connectorId ?? 1,
                                            type: socketConnector.onByUser ? 'Inoperative' : 'Operative',
                                        },
                                    })
                                        .unwrap()
                                        .then(() =>
                                            setSocketConnector((prevValue) => ({
                                                ...prevValue,
                                                onByUser: !prevValue?.onByUser,
                                                offByUser: !prevValue?.offByUser,
                                            }))
                                        )
                                        .catch(() => toast.notifyError(text('msgRemoteControlCommandFailed')))
                                }
                                isToggled={socketConnector.onByUser}
                                disabled={isReadOnly || !isOnline || chargePoint?.ocppEnabled}
                                data-testid={
                                    socketConnector.onByUser
                                        ? `socket-On-${chargePoint.id}`
                                        : `socket-Off-${chargePoint.id}`
                                }
                            />
                        }
                        status={
                            isOnline && (
                                <ConnectorStatus
                                    connector={socketConnector}
                                    isOcpp={chargePoint?.isOcpp}
                                    key={socketConnector?.connectorId}
                                    showConsumption={true}
                                    showErrorStatus={true}
                                    productId={chargePoint?.productId}
                                />
                            )
                        }
                    />
                    {isSupportDomain ? (
                        <RemoteControlRow
                            title={
                                <div data-testid={`chargerScheduleText-${chargePoint.id}`}>
                                    {text('lblSocketSchedule')}
                                </div>
                            }
                            button={
                                <Toggle
                                    toggleFn={handleScheduleUpdate(socketConnector?.connectorId ?? 1)}
                                    isToggled={checkScheduleIsActive(socketConnector?.connectorId ?? 1)}
                                    disabled={isReadOnly || !isOnline || chargePoint?.ocppEnabled}
                                    data-testid={
                                        checkScheduleIsActive(socketConnector?.connectorId ?? 1)
                                            ? `socketSchedule-On-${chargePoint.id}`
                                            : `socketSchedule-Off-${chargePoint.id}`
                                    }
                                />
                            }
                        />
                    ) : null}
                </>
            )}
            <RemoteControlRow
                title={
                    <div data-testid={`lightText-${chargePoint.id}`}>
                        <Text code="lblLight" data-testid={`lightText-${chargePoint.id}`} />
                    </div>
                }
                button={
                    <Toggle
                        toggleFn={() =>
                            toggleOcppConfigValue(KEY_DOWN_LIGHT, isLightOn ? LightState.OFF : LightState.ON)
                        }
                        isToggled={isLightOn}
                        disabled={isReadOnly || !isOnline || chargePoint?.ocppEnabled}
                        data-testid={`light-${chargePoint.id}`}
                    />
                }
            />
            <RemoteOcppDimmerControl chargePoint={chargePoint} isReadOnly={isReadOnly} />
            <RemoteControlRow
                title={
                    <div data-testid={`rfidText-${chargePoint.id}`}>
                        <Text code="lblRFIDLock" />
                    </div>
                }
                button={
                    <Toggle
                        toggleFn={() =>
                            toggleOcppConfigValue(KEY_FREE_CHARGING, isNfcLockOn ? NfcLockState.OFF : NfcLockState.ON)
                        }
                        isToggled={isNfcLockOn}
                        disabled={
                            isReadOnly ||
                            onGoingTransactionStates.includes(chargePoint.connectors[0]?.ocppStatus) ||
                            !isOnline ||
                            chargePoint?.ocppEnabled
                        }
                        data-testid={`rfid-${chargePoint.id}`}
                    />
                }
            />
            <SelectRFIDTagModal
                isOpen={selectRfidModalIsOpen}
                chargePointId={chargePoint.id}
                connectorId={chargingConnector?.connectorId ?? 1}
                onStartCharging={(rfidTag) => {
                    handleRemoteStart(rfidTag)
                    setSelectRfidModalIsOpen(false)
                }}
                onClose={() => setSelectRfidModalIsOpen(false)}
            />
        </RemoteControlWidget>
    )
}
